import { roundToStrike } from "@/lib/options/structure";
import type { Mode, Regime } from "@/types/options";
import { defineStrategy } from "./define";
import { leg } from "./leg";
import { offsetParam, toggleParam } from "./params";
import { scale } from "./scale";

// ── Iron Fly ────────────────────────────────────────────────────────────────
// side: -1 = standard (short center, long wings)
// side:  1 = inverse (long center, short wings)

export const ironFly = defineStrategy("iron_fly", "Iron Fly", "multi_leg", {
  defaultParams: () => ({
    width: 50,
    skew: 0,
    side: -1,
  }),
  paramSchema: [
    toggleParam("side", "Side", [
      [-1, "Short"],
      [1, "Long"],
    ]),
    offsetParam("width", "Wing Width", 15, 130),
    { key: "skew", label: "Skew", min: -1, max: 1, step: 0.05 },
  ],
  scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
    width: scale(50, r, m, true),
    skew: cur.skew ?? 0,
    side: cur.side ?? -1,
  }),
  toLegs: (spot, params, strikeStep) => {
    const center = roundToStrike(spot, strikeStep);
    const skewOffset = roundToStrike(params.skew * 25, strikeStep);
    const mid = center + skewOffset;
    const w = params.width;
    const centerSide: "long" | "short" = params.side >= 0 ? "long" : "short";
    const wingSide: "long" | "short" = params.side >= 0 ? "short" : "long";
    return [
      leg(mid, "call", centerSide),
      leg(mid, "put", centerSide),
      leg(mid + w, "call", wingSide),
      leg(mid - w, "put", wingSide),
    ];
  },
});

// ── Broken Wing Butterfly ───────────────────────────────────────────────────

export const brokenWingButterfly = defineStrategy(
  "broken_wing_butterfly",
  "Broken Wing Butterfly",
  "multi_leg",
  {
    defaultParams: () => ({
      direction: 1,
      s1: 45,
      s2: 100,
      l2: 135,
    }),
    paramSchema: [
      toggleParam("direction", "Direction", [
        [-1, "Lower"],
        [1, "Upper"],
      ]),
      offsetParam("s1", "Short 1 Offset", 15, 100, 1),
      offsetParam("s2", "Short 2 Offset", 40, 180, 2),
      offsetParam("l2", "Long Wing Offset", 60, 220, 3),
    ],
    scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
      direction: cur.direction ?? 1,
      s1: scale(45, r, m, true),
      s2: scale(100, r, m, true),
      l2: scale(135, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      const { direction, s1, s2, l2 } = params;
      const sign = direction >= 0 ? 1 : -1;
      const optType: "call" | "put" = sign > 0 ? "call" : "put";
      return [
        leg(center, optType, "long"),
        leg(center + sign * s1, optType, "short"),
        leg(center + sign * s2, optType, "short"),
        leg(center + sign * l2, optType, "long"),
      ];
    },
  },
);

// ── Iron Condor ─────────────────────────────────────────────────────────────
// side: -1 = standard (short inner, long outer)
// side:  1 = inverse (long inner, short outer)

export const ironCondor = defineStrategy(
  "iron_condor",
  "Iron Condor",
  "multi_leg",
  {
    defaultParams: () => ({
      putShortOffset: 30,
      putLongOffset: 50,
      callShortOffset: 30,
      callLongOffset: 50,
      side: -1,
    }),
    paramSchema: [
      toggleParam("side", "Side", [
        [-1, "Short"],
        [1, "Long"],
      ]),
      offsetParam("putShortOffset", "Put Short Offset", 5, 100, 0),
      offsetParam("putLongOffset", "Put Long Offset", 10, 150, 1),
      offsetParam("callShortOffset", "Call Short Offset", 5, 100, 2),
      offsetParam("callLongOffset", "Call Long Offset", 10, 150, 3),
    ],
    scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
      putShortOffset: scale(30, r, m, true),
      putLongOffset: scale(50, r, m, true),
      callShortOffset: scale(30, r, m, true),
      callLongOffset: scale(50, r, m, true),
      side: cur.side ?? -1,
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      const innerSide: "long" | "short" = params.side >= 0 ? "long" : "short";
      const outerSide: "long" | "short" = params.side >= 0 ? "short" : "long";
      return [
        leg(center - params.putShortOffset, "put", innerSide),
        leg(center - params.putLongOffset, "put", outerSide),
        leg(center + params.callShortOffset, "call", innerSide),
        leg(center + params.callLongOffset, "call", outerSide),
      ];
    },
  },
);

// ── Butterfly ─────────────────────────────────────────────────────────────
// side:  1 = long butterfly (long wings, short 2x center)
// side: -1 = short butterfly (short wings, long 2x center)

export const butterfly = defineStrategy("butterfly", "Butterfly", "multi_leg", {
  defaultParams: () => ({
    width: 30,
    optionType: 0,
    side: 1,
  }),
  paramSchema: [
    toggleParam("side", "Side", [
      [-1, "Short"],
      [1, "Long"],
    ]),
    offsetParam("width", "Wing Width", 5, 100),
    toggleParam("optionType", "Type", [
      [0, "Call"],
      [1, "Put"],
    ]),
  ],
  scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
    width: scale(30, r, m, true),
    optionType: cur.optionType ?? 0,
    side: cur.side ?? 1,
  }),
  toLegs: (spot, params, strikeStep) => {
    const center = roundToStrike(spot, strikeStep);
    const t: "call" | "put" = params.optionType >= 1 ? "put" : "call";
    const w = params.width;
    const wingSide: "long" | "short" = params.side >= 0 ? "long" : "short";
    const centerSide: "long" | "short" = params.side >= 0 ? "short" : "long";
    return [
      leg(center - w, t, wingSide),
      leg(center, t, centerSide, 2),
      leg(center + w, t, wingSide),
    ];
  },
});

// ── Long Condor ─────────────────────────────────────────────────────────────
// 4-leg condor using a single option type. Call/put toggle.

export const longCondor = defineStrategy(
  "long_condor",
  "Long Condor",
  "multi_leg",
  {
    defaultParams: () => ({
      innerLow: 15,
      innerHigh: 30,
      outerWidth: 15,
      optionType: 0,
    }),
    paramSchema: [
      toggleParam("optionType", "Type", [
        [0, "Call"],
        [1, "Put"],
      ]),
      offsetParam("innerLow", "Lower Body", 5, 60),
      offsetParam("innerHigh", "Upper Body", 5, 60),
      offsetParam("outerWidth", "Wing Width", 5, 60),
    ],
    scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
      innerLow: scale(15, r, m, true),
      innerHigh: scale(30, r, m, true),
      outerWidth: scale(15, r, m, false),
      optionType: cur.optionType ?? 0,
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      const t: "call" | "put" = params.optionType >= 1 ? "put" : "call";
      return [
        leg(center - params.innerLow - params.outerWidth, t, "long"),
        leg(center - params.innerLow, t, "short"),
        leg(center + params.innerHigh, t, "short"),
        leg(center + params.innerHigh + params.outerWidth, t, "long"),
      ];
    },
  },
);

// ── Short Condor ────────────────────────────────────────────────────────────
// Inverted condor using a single option type. Call/put toggle.

export const shortCondor = defineStrategy(
  "short_condor",
  "Short Condor",
  "multi_leg",
  {
    defaultParams: () => ({
      innerLow: 15,
      innerHigh: 30,
      outerWidth: 15,
      optionType: 0,
    }),
    paramSchema: [
      toggleParam("optionType", "Type", [
        [0, "Call"],
        [1, "Put"],
      ]),
      offsetParam("innerLow", "Lower Body", 5, 60),
      offsetParam("innerHigh", "Upper Body", 5, 60),
      offsetParam("outerWidth", "Wing Width", 5, 60),
    ],
    scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
      innerLow: scale(15, r, m, true),
      innerHigh: scale(30, r, m, true),
      outerWidth: scale(15, r, m, false),
      optionType: cur.optionType ?? 0,
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      const t: "call" | "put" = params.optionType >= 1 ? "put" : "call";
      return [
        leg(center - params.innerLow - params.outerWidth, t, "short"),
        leg(center - params.innerLow, t, "long"),
        leg(center + params.innerHigh, t, "long"),
        leg(center + params.innerHigh + params.outerWidth, t, "short"),
      ];
    },
  },
);
